import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Zap, Star, CheckCircle2, Loader2, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrderFeedback — public, no-auth required.
 * Linked to from the post-delivery SMS/WhatsApp/Email asking the
 * customer whether the order arrived on time. Backed by
 * POST /api/fast/orders/{id}/verify.
 */
const OrderFeedback = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  // Form state
  const [onTime, setOnTime] = useState(null);   // true | false
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!id) return;
    axios.get(`${API}/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setError(t('feedback.not_found', 'We could not find this order.')))
      .finally(() => setLoading(false));
  }, [id, t]);

  // Rating is OPTIONAL per the simplified spec (2026-06-08): the
  // customer only needs to confirm 👍/👎. Star rating + comment remain
  // available as a "Tell us more" follow-up but never block submission.
  const canSubmit = onTime !== null && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/fast/orders/${id}/verify`, {
        on_time: onTime,
        // Only send rating when the customer actually picked one — the
        // backend now treats `rating` as nullable and skips the rating
        // counters when absent.
        rating: rating >= 1 ? rating : null,
        comment: comment.trim() || null,
        customer_email: order?.customer_email || null,
      });
      setDone(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t('feedback.error', 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
      </div>
    );
  }
  if (error && !order) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center text-white">
          <p className="text-lg font-bold">{error}</p>
          <Link to="/restaurants" className="inline-block mt-4 text-yellow-400 underline">
            {t('feedback.back_home', 'Back to NoFee')}
          </Link>
        </div>
      </div>
    );
  }
  const promised = order?.promised_minutes;
  const restaurantName = order?.restaurant_name || '';

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="feedback-success">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            {t('feedback.thanks_title', 'Thanks for your feedback!')}
          </h1>
          <p className="text-white/65 mt-2">
            {t('feedback.thanks_body', 'Your verification helps customers find reliable restaurants.')}
          </p>
          {done.rewards_credited && (
            <div
              data-testid="feedback-rewards-credited"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500/15 border border-emerald-500/35 text-emerald-300 text-sm font-bold"
            >
              <Gift className="w-4 h-4" />
              +{done.rewards_amount} {t('feedback.rewards_earned', 'NoFee Rewards Points earned')}
            </div>
          )}
          <Link
            to="/restaurants"
            className="block mt-8 text-yellow-400 font-semibold underline underline-offset-4"
          >
            {t('feedback.find_more', 'Find more restaurants')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="feedback-page">
      <div className="max-w-md mx-auto px-5 py-10 sm:py-14">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/25 text-white text-[11px] font-black uppercase tracking-[0.18em] mb-3">
            <Zap className="w-3 h-3 text-yellow-400 fill-yellow-400" /> Fast by NoFee
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
            {t('feedback.title', 'How was your order?')}
          </h1>
          {restaurantName && (
            <p className="text-white/55 text-sm mt-2">
              {t('feedback.order_from', { defaultValue: 'Order from {{name}}', name: restaurantName })}
            </p>
          )}
        </div>

        {/* On-time question */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 mb-4">
          <p className="text-sm font-bold text-white mb-1">
            {promised
              ? t('feedback.question_with_time', {
                  defaultValue: 'Did your order arrive within ~{{minutes}} min as promised?',
                  minutes: promised,
                })
              : t('feedback.question_simple', 'Did your order arrive on time?')}
          </p>
          <p className="text-[12px] text-white/45 mb-4">
            {t('feedback.helper', 'Your honest answer keeps NoFee transparent.')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setOnTime(true)}
              data-testid="feedback-yes"
              className={`py-3 rounded-xl font-bold text-sm border-2 transition-all ${
                onTime === true
                  ? 'bg-emerald-500 text-zinc-950 border-emerald-500'
                  : 'bg-white/[0.02] text-white/85 border-white/15 hover:border-emerald-400/50'
              }`}
            >
              {t('feedback.yes', 'Yes')}
            </button>
            <button
              type="button"
              onClick={() => setOnTime(false)}
              data-testid="feedback-no"
              className={`py-3 rounded-xl font-bold text-sm border-2 transition-all ${
                onTime === false
                  ? 'bg-rose-500 text-white border-rose-500'
                  : 'bg-white/[0.02] text-white/85 border-white/15 hover:border-rose-400/50'
              }`}
            >
              {t('feedback.no', 'No')}
            </button>
          </div>
        </div>

        {/* Rating */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 mb-4">
          <p className="text-sm font-bold text-white mb-3">
            {t('feedback.rate_experience', 'Rate your experience')}
          </p>
          <div className="flex justify-center gap-1.5" data-testid="feedback-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`feedback-star-${n}`}
                className="p-1.5"
                aria-label={`${n} stars`}
              >
                <Star
                  className={`w-9 h-9 transition-all ${
                    (hover || rating) >= n
                      ? 'fill-yellow-400 text-yellow-400 scale-110'
                      : 'text-white/25'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Optional comment */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 mb-5">
          <label className="block text-sm font-bold text-white mb-1">
            {t('feedback.leave_comment', 'Leave a comment')}
            <span className="text-white/45 font-normal ml-1.5">{t('feedback.optional', '(optional, +1 extra point)')}</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('feedback.comment_placeholder', 'Food was great and arrived on time…')}
            rows={3}
            maxLength={500}
            data-testid="feedback-comment"
            className="w-full bg-zinc-900 border border-white/12 focus:border-yellow-400 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/35 outline-none resize-none transition-colors"
          />
        </div>

        {error && (
          <p className="text-[12px] text-rose-400 text-center mb-3" data-testid="feedback-error">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="feedback-submit"
          className="w-full py-3.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 active:scale-[0.99] text-zinc-950 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t('feedback.submit', 'Submit verification')}
        </button>
        <p className="text-[11px] text-white/35 text-center mt-3">
          {t('feedback.privacy', "We'll never share your contact info. One verification per order.")}
        </p>
      </div>
    </div>
  );
};

export default OrderFeedback;
